import { API_ENDPOINTS, PLAYS_API } from '@/constants/api-endpoints';
import {
  addPlaysPhoneNumber,
  checkPlayName,
  createAIAgent,
  createEmailTemplate,
  deleteAIAgent,
  getAIAgentById,
  getAIAgents,
  getAllCalendarsByUser,
  getAllPlays,
  getAvatars,
  getCampaignResults,
  getEmailDynamicVariables,
  getEmailTemplates,
  getEmailThread,
  getHeygenVoices,
  getPlayDetails,
  getPlayExecution,
  getPlayListings,
  getGroupedPlayListings,
  getPlayResults,
  getPlaysPhoneNumber,
  getPlaysByAudienceId,
  getRecentActivity,
  getRunHistory,
  getRunDetails,
  getHeyReachPendingStepLeadStatus,
  publishPlay,
  deleteCampaignFromAudience,
  removeAudiencePlay,
  moveAudiencePlay,
  removePlay,
  storeAudiencePlay,
  updateAIAgent,
  updateAudiencePlay,
  updateEmailTemplate,
  uploadPlayImage,
  getAvatarGroups,
  getAvatarsInGroup,
  uploadAssetToHeyGen,
  patchAudiencePlayNodeWithResult,
  copyTemplateToAudience,
} from '@/services/plays';
import { announceViewProfileStep } from '@/utils/linkedinViewBeforeConnect';
import {
  ICreateAIAgentBody,
  ICreateEmailTemplateBody,
  IPlayResultDetail,
  IStoreAudiencePLayData,
} from '@/types/plays';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts';

export const useGetAllPlays = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.getAllPlays],
    queryFn: getAllPlays,
    enabled: isAuthenticated,
  });
};

export const useGetPlayListings = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.getPlayListings],
    queryFn: getPlayListings,
    enabled: isAuthenticated,
  });
};

const useGetGroupedPlayListings = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.getPlayListings, 'grouped'],
    queryFn: getGroupedPlayListings,
    enabled: isAuthenticated,
  });
};

/**
 * Invalidate the authoritative workflow-structure caches after a structural
 * mutation (#1810). Without this, create/update/delete/move only mutated local
 * React Flow state and the getPlayDetails cache (real parent_node + sequence
 * order) was never refetched — the editor's belief could permanently diverge
 * from the DB. getPlayDetails is keyed by the workflow/audience id which the
 * remove path does not have, so match by key prefix via predicate.
 */
export const invalidateWorkflowStructure = async (
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  await queryClient.invalidateQueries({
    predicate: (q) =>
      typeof q.queryKey?.[0] === 'string' &&
      ((q.queryKey[0] as string).startsWith('/private/plays/details/') ||
        (q.queryKey[0] as string).startsWith('/private/plays/audience/')),
  });
  await queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getPlayListings] });
};

export const useStoreAudiencePlay = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: storeAudiencePlay,
    onSuccess: (data) => {
      // A new connection request may have had a View profile step put in front
      // of it; the structure refetch below brings those nodes onto the canvas.
      announceViewProfileStep(data?.viewProfileStep);
      return invalidateWorkflowStructure(queryClient);
    },
  });
};

export const useGetPlayDetails = (id: number) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.getPlayDetails(id), id],
    queryFn: () => getPlayDetails(id),
    enabled: !!id && isAuthenticated,
  });
};

/**
 * Hook to fetch workflow structure (plays) by audience ID
 * Used for client-side workflow cloning
 */
export const useGetPlaysByAudienceId = (audienceId: number) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.getPlaysByAudienceId(audienceId), audienceId],
    queryFn: () => getPlaysByAudienceId(audienceId),
    enabled: !!audienceId && isAuthenticated,
  });
};

export const useUpdateAudiencePlay = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: IStoreAudiencePLayData }) =>
      updateAudiencePlay(id, data),
    onSuccess: () => invalidateWorkflowStructure(queryClient),
  });
};

export const useRemoveAudiencePlay = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeAudiencePlay,
    onSuccess: () => invalidateWorkflowStructure(queryClient),
  });
};

/**
 * Reparent a node via the dedicated, safe move endpoint. Use this for every
 * drag-to-reparent gesture instead of PUT /plays/{id} (which no longer accepts
 * parentNode and previously corrupted execution order + renamed the node).
 */
export const useMoveAudiencePlay = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, parentNode }: { id: number; parentNode: number | null }) =>
      moveAudiencePlay(id, parentNode),
    onSuccess: () => invalidateWorkflowStructure(queryClient),
  });
};

export const useRemovePlay = () => {
  const queryClient = useQueryClient();
  const fn = async ({ playName, audienceId }: { playName: string; audienceId: number }) => {
    const res = await removePlay({
      playName,
      audienceId,
    });
    await queryClient.invalidateQueries({
      queryKey: [API_ENDPOINTS.getPlayListings],
    });
    return res;
  };

  return useMutation({
    mutationFn: fn,
  });
};

export const useDeleteCampaignFromAudience = () => {
  const queryClient = useQueryClient();
  const fn = async (audienceId: number) => {
    const res = await deleteCampaignFromAudience(audienceId);
    await queryClient.invalidateQueries({
      queryKey: [API_ENDPOINTS.getPlayListings],
    });
    await queryClient.invalidateQueries({
      queryKey: [API_ENDPOINTS.getAllAudiences],
    });
    await queryClient.invalidateQueries({
      queryKey: [API_ENDPOINTS.getAudienceById(audienceId)],
    });
    return res;
  };

  return useMutation({
    mutationFn: fn,
  });
};

/**
 * Apply a system template's plays to an EXISTING audience. See
 * services/plays.ts :: copyTemplateToAudience for the 1:1-model rationale.
 * Invalidates both the play listings and the audience row cache so the
 * target audience's workflow_count / first_workflow_name reflect the new
 * attachment immediately on the next render.
 */
export const useCopyTemplateToAudience = () => {
  const queryClient = useQueryClient();
  const fn = async ({
    sourceAudienceId,
    targetAudienceId,
  }: {
    sourceAudienceId: number;
    targetAudienceId: number;
  }) => {
    const res = await copyTemplateToAudience({
      sourceAudienceId,
      targetAudienceId,
    });
    await queryClient.invalidateQueries({
      queryKey: [API_ENDPOINTS.getPlayListings],
    });
    await queryClient.invalidateQueries({
      queryKey: [API_ENDPOINTS.getAudienceById(targetAudienceId)],
    });
    return res;
  };

  return useMutation({
    mutationFn: fn,
  });
};

export const useCheckPlayName = () => {
  return useMutation({
    mutationFn: checkPlayName,
  });
};

export const useGetAIAgents = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.getAIAgents],
    queryFn: getAIAgents,
    enabled: isAuthenticated,
  });
};

export const useCreateAIAgent = () => {
  const queryClient = useQueryClient();
  const mutationFn = async (data: ICreateAIAgentBody) => {
    const res = await createAIAgent(data);
    await queryClient.invalidateQueries({
      queryKey: [API_ENDPOINTS.getAIAgents],
    });

    return res;
  };

  return useMutation({
    mutationFn,
  });
};

export const useUpdateAIAgent = () => {
  const queryClient = useQueryClient();
  const mutationFn = async (body: { id: number; data: ICreateAIAgentBody }) => {
    const res = await updateAIAgent(body);
    await queryClient.invalidateQueries({
      queryKey: [API_ENDPOINTS.getAIAgents],
    });

    return res;
  };

  return useMutation({
    mutationFn,
  });
};

export const useGetAIAgentById = (id: number) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.getAIAgentById(id), id],
    queryFn: () => getAIAgentById(id),
    enabled: !!id && isAuthenticated,
  });
};

export const usePublishPlay = () => {
  return useMutation({
    mutationFn: publishPlay,
  });
};

export const useGetPlaysPhoneNumber = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.getPlaysPhoneNumber],
    queryFn: getPlaysPhoneNumber,
    enabled: isAuthenticated,
  });
};

export const useAddPlaysPhoneNumber = () => {
  const queryClient = useQueryClient();
  const fn = async (data: { areaCode: string; cnamEnabled: boolean }) => {
    const res = await addPlaysPhoneNumber(data);
    await queryClient.invalidateQueries({
      queryKey: [API_ENDPOINTS.getPlaysPhoneNumber],
    });
    return res;
  };

  return useMutation({
    mutationFn: fn,
  });
};

export const useGetEmailDynamicVariables = (audienceId?: number | string) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    // audienceId in the key so switching between audiences (or opening a picker before one
    // is selected) doesn't serve another audience's cached, list-scoped variables/samples.
    queryKey: [API_ENDPOINTS.getEmailDynamicVariables, audienceId ?? null],
    queryFn: () => getEmailDynamicVariables(audienceId),
    enabled: isAuthenticated,
  });
};

export const useGetEmailTemplates = (category: 'email' | 'video') => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.getEmailTemplates, category],
    queryFn: () => getEmailTemplates(category),
    enabled: isAuthenticated,
  });
};

export const useCreateEmailTemplate = () => {
  const queryClient = useQueryClient();
  const fn = async ({
    data,
    category,
  }: {
    data: ICreateEmailTemplateBody;
    category: 'email' | 'video';
  }) => {
    const res = await createEmailTemplate(data, category);
    await queryClient.invalidateQueries({
      queryKey: [API_ENDPOINTS.getEmailTemplates, category],
    });

    return res;
  };

  return useMutation({
    // Keyed so the builder's launch barrier can see a template save still in flight.
    mutationKey: ['createEmailTemplate'],
    mutationFn: fn,
  });
};

export const useUpdateEmailTemplate = () => {
  const queryClient = useQueryClient();
  const fn = async (body: { data: ICreateEmailTemplateBody; id: number }) => {
    const res = await updateEmailTemplate(body);
    await queryClient.invalidateQueries({
      queryKey: [API_ENDPOINTS.getEmailTemplates],
    });

    return res;
  };

  return useMutation({
    // Keyed so the builder's launch barrier can see a template save still in flight.
    mutationKey: ['updateEmailTemplate'],
    mutationFn: fn,
  });
};

/**
 * New hook for campaign results using hierarchical structure
 * Use this for new components - returns plays grouped with their executions
 */
const useGetCampaignResults = (
  audienceId: number,
  params: {
    startDate?: string;
    endDate?: string;
    status?: 'completed' | 'failed' | 'in_progress' | 'pending';
    playTypes?: string[];
    includeMetrics?: boolean;
    includeResults?: boolean;
    page?: number;
    pageSize?: number;
  } = {},
) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['campaign-results', audienceId, ...Object.values(params)],
    queryFn: () => getCampaignResults(audienceId, params),
    enabled: !!audienceId && isAuthenticated,
  });
};

/**
 * Legacy hook - still using old flattened structure
 * @deprecated Use useGetCampaignResults instead for new components
 */
export const useGetPlayResults = (
  id: number,
  params: {
    playName?: string;
    playId?: number;
    page?: number;
    pageSize?: number;
    playStatus?: IPlayResultDetail['status'];
    playType?: IPlayResultDetail['type'];
  },
) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.getPlayResults(id), id, ...Object.values(params)],
    queryFn: () => getPlayResults(id, params),
    enabled: !!id && isAuthenticated,
  });
};

const useGetPlayExecution = (
  id: number,
  params: {
    page?: number;
    pageSize?: number;
  },
  enabled?: boolean,
) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.getPlayExecution(id), id, ...Object.values(params)],
    queryFn: () => getPlayExecution(id, params),
    enabled: !!id && enabled && isAuthenticated,
  });
};

/**
 * Get run history for an audience
 * Lists all workflow runs grouped by execution_sequence
 */
export const useGetRunHistory = (
  audienceId: number,
  params: {
    page?: number;
    pageSize?: number;
    startDate?: string;
    allHistory?: boolean;
  } = {},
  enabled: boolean = true,
) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['run-history', audienceId, params.page, params.pageSize, params.startDate, params.allHistory],
    queryFn: () => getRunHistory(audienceId, params),
    enabled: !!audienceId && isAuthenticated && enabled,
  });
};

/**
 * Get detailed results for a specific run
 * Returns all play executions for a given run number
 */
export const useGetRunDetails = (
  audienceId: number,
  runNumber: number,
  enabled: boolean = true,
) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['run-details', audienceId, runNumber],
    queryFn: () => getRunDetails(audienceId, runNumber),
    enabled: !!audienceId && !!runNumber && enabled && isAuthenticated,
  });
};

/**
 * Per-lead HeyReach status for a pending step, fetched only when the operator
 * expands it — heyreach_lead_statuses can be large, so this is not part of the
 * main run-details payload.
 */
export const useGetHeyReachPendingStepLeadStatus = (
  audienceId: number,
  runNumber: number,
  audiencePlayId: number | null,
  enabled: boolean = true,
) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['heyreach-pending-step-lead-status', audienceId, runNumber, audiencePlayId],
    queryFn: () => getHeyReachPendingStepLeadStatus(audienceId, runNumber, audiencePlayId as number),
    enabled: !!audienceId && !!runNumber && !!audiencePlayId && enabled && isAuthenticated,
    staleTime: 60_000,
  });
};

/**
 * Fetch audiences with recent play execution activity.
 * Used by the notification drawer to discover runs the user missed in real-time.
 */
export const useGetRecentActivity = (enabled: boolean = true) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    // 72h window (server cap) so the drawer mirrors the campaigns a user sees in /campaigns,
    // not just runs from the last day. Scoped server-side to the current user's own
    // non-ephemeral campaigns.
    queryKey: ['recent-activity'],
    queryFn: () => getRecentActivity({ hours: 72 }),
    enabled: enabled && isAuthenticated,
    staleTime: 30_000,
  });
};

export const useUploadPlayImage = () => {
  return useMutation({
    mutationFn: uploadPlayImage,
  });
};

const useGetAvatars = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.heygenAvatars],
    queryFn: getAvatars,
    enabled: isAuthenticated,
  });
};

export const useGetHeygenVoices = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.heygenVoices],
    queryFn: getHeygenVoices,
    enabled: isAuthenticated,
    retry: false, // Don't retry on missing credentials
    meta: {
      // Silent error handling - component will display fallback UI
      errorMessage: 'HeyGen voices unavailable. Please configure HeyGen in Settings.',
    },
  });
};

export const useGetAllCalendarsByUser = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.getAllCalendarsByUser],
    queryFn: getAllCalendarsByUser,
    enabled: isAuthenticated,
  });
};

export const useDeleteAIAgent = () => {
  const queryClient = useQueryClient();

  const fn = async (id: number) => {
    const res = await deleteAIAgent(id);
    await queryClient.invalidateQueries({
      queryKey: [API_ENDPOINTS.getAIAgents],
    });
    return res;
  };

  return useMutation({
    mutationFn: fn,
  });
};

export const useGetAvatarGroups = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.heygenAvatarGroups],
    queryFn: getAvatarGroups,
    enabled: isAuthenticated,
    retry: false, // Don't retry on missing credentials
    meta: {
      // Silent error handling - component will display fallback UI
      errorMessage: 'HeyGen avatars unavailable. Please configure HeyGen in Settings.',
    },
  });
};

export const useGetAvatarsInGroup = (groupId: string, options?: { enabled?: boolean }) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.heygenAvatarsInGroup, groupId],
    queryFn: () => getAvatarsInGroup(groupId),
    enabled: (options?.enabled ?? !!groupId) && isAuthenticated,
    retry: false, // Don't retry on missing credentials
    meta: {
      // Silent error handling - component will display fallback UI
      errorMessage: 'HeyGen avatars unavailable. Please configure HeyGen in Settings.',
    },
  });
};

export const useUploadAssetToHeyGen = () => {
  return useMutation({
    mutationFn: uploadAssetToHeyGen,
  });
};

/**
 * Per workflow, the nodes saves made the server add (each View profile step and its Delay) that
 * the canvas has not shown yet. They accumulate until the builder has them on the canvas
 * (consumeViewProfileNodeIds): the builder merges exactly these in place, and any other
 * structural change rebuilds the canvas. Every PATCH cancels the details query in onMutate, so a
 * later PATCH can cancel the re-read; each PATCH that settles asks for it again while any of
 * these is missing from the details cache.
 */
const pendingViewProfileNodes = new Map<number, Set<number>>();

/**
 * @param workflowId - The workflow (audience) id
 * @returns audience_plays ids of View profile steps and Delays saves added, not yet on the canvas
 */
export const awaitedViewProfileNodeIds = (workflowId?: number): ReadonlySet<number> => (
  new Set(workflowId ? pendingViewProfileNodes.get(workflowId) || [] : [])
);

/**
 * The canvas now shows these nodes; stop treating them as pending.
 *
 * @param workflowId - The workflow (audience) id
 * @param ids - audience_plays ids now on the canvas
 */
export const consumeViewProfileNodeIds = (workflowId: number | undefined, ids: Iterable<number>): void => {
  const pending = workflowId ? pendingViewProfileNodes.get(workflowId) : undefined;
  if (!workflowId || !pending) return;
  for (const id of ids) pending.delete(id);
  if (pending.size === 0) pendingViewProfileNodes.delete(workflowId);
};

const resyncAwaitedStructure = (
  queryClient: ReturnType<typeof useQueryClient>,
  workflowId: number | undefined,
) => {
  const pending = workflowId ? pendingViewProfileNodes.get(workflowId) : undefined;
  if (!workflowId || !pending || pending.size === 0) return;
  const cached = queryClient.getQueryData<{ plays?: Array<{ id?: number }> }>(
    [API_ENDPOINTS.getPlayDetails(workflowId), workflowId],
  );
  const cachedIds = new Set((cached?.plays || []).map((play) => play.id));
  if ([...pending].some((id) => !cachedIds.has(id))) invalidateWorkflowStructure(queryClient);
};

export const usePatchAudiencePlayNode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // Keyed so the builder can tell when a node save is in flight (useIsMutating).
    mutationKey: ['patchAudiencePlayNode'],
    mutationFn: ({
      nodeId,
      updates,
    }: {
      nodeId: number;
      updates: {
        title?: string;
        description?: string;
        customActionDetails?: Record<string, unknown>;
      };
      workflowId?: number;
    }) => patchAudiencePlayNodeWithResult(nodeId, updates),

    // Optimistic updates for instant UI feedback
    onMutate: async ({ nodeId, updates, workflowId }) => {
      // We need the workflowId to update the correct cache entry
      if (!workflowId) {
        // If no workflowId provided, we can't do optimistic updates
        return {};
      }

      const queryKey = [API_ENDPOINTS.getPlayDetails(workflowId), workflowId];

      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousPlayDetails = queryClient.getQueryData(queryKey);

      // Optimistically update the cache
      queryClient.setQueryData(queryKey, (old: unknown) => {
        const oldData = old as { plays?: Array<Record<string, unknown>> };
        if (!oldData?.plays) return old;

        return {
          ...oldData,
          plays: oldData.plays.map((play: Record<string, unknown>) => {
            if (play.id === nodeId) {
              const updatedPlay = { ...play };

              // Update individual fields
              if (updates.title !== undefined) {
                updatedPlay.title = updates.title;
                updatedPlay.play_name = updates.title; // Also update play_name field
              }
              if (updates.description !== undefined) {
                updatedPlay.description = updates.description;
                updatedPlay.play_description = updates.description; // Also update play_description field
              }
              if (updates.customActionDetails !== undefined) {
                // Deep merge custom action details
                updatedPlay.custom_action_details = {
                  ...(typeof updatedPlay.custom_action_details === 'object' && updatedPlay.custom_action_details !== null ? updatedPlay.custom_action_details : {}),
                  ...updates.customActionDetails,
                };
              }

              // Update timestamp
              updatedPlay.play_updated_at = new Date().toISOString();

              return updatedPlay;
            }
            return play;
          }),
        };
      });

      // Return a context object with the snapshotted value
      return { previousPlayDetails, workflowId };
    },

    // CRITICAL FIX: On error, rollback optimistic update and force refetch
    onError: (err, variables, context) => {
      if (context?.previousPlayDetails && context?.workflowId) {
        const queryKey = [API_ENDPOINTS.getPlayDetails(context.workflowId), context.workflowId];

        // Rollback to previous state
        queryClient.setQueryData(queryKey, context.previousPlayDetails);

        // Force refetch to get latest server state
        queryClient.invalidateQueries({
          queryKey: [API_ENDPOINTS.getPlayDetails(context.workflowId), context.workflowId],
        });
      }
    },

    // CRITICAL FIX: On success, update cache with server response to ensure consistency
    onSuccess: ({ node: serverResponse, viewProfileStep }, variables, context) => {
      if (context?.workflowId) {
        const queryKey = [API_ENDPOINTS.getPlayDetails(context.workflowId), context.workflowId];

        // Update cache with authoritative server response
        queryClient.setQueryData(queryKey, (old: unknown) => {
          const oldData = old as { plays?: Array<Record<string, unknown>> };
          if (!oldData?.plays) return old;

          return {
            ...oldData,
            plays: oldData.plays.map((play: Record<string, unknown>) => {
              if (play.id === variables.nodeId) {
                // Merge server response, but preserve read-time-enriched fields the PATCH
                // endpoint doesn't return. templateName is joined from email_templates at
                // read time by plays/details — it's not stored in the DB column, so the
                // raw PATCH response always omits it.
                const oldDetails = (typeof play.custom_action_details === 'object' && play.custom_action_details !== null)
                  ? (play.custom_action_details as Record<string, unknown>)
                  : {};
                const serverDetails = (serverResponse.custom_action_details as Record<string, unknown> | undefined) ?? {};
                const mergedDetails: Record<string, unknown> = { ...serverDetails };
                if (!mergedDetails.templateName && oldDetails.templateName) {
                  mergedDetails.templateName = oldDetails.templateName;
                }
                return {
                  ...play,
                  ...serverResponse,
                  custom_action_details: mergedDetails,
                  title: serverResponse.title,
                  description: serverResponse.description,
                  updated_at: serverResponse.updated_at,
                };
              }
              return play;
            }),
          };
        });

        // CRITICAL FIX: Do NOT invalidate queries here!
        // setQueryData above already updated the cache with authoritative server response.
        // Invalidating triggers a refetch that can return stale data due to database
        // replication lag or transaction timing, causing form to revert to old values.
        // The cache is already correct from the PATCH response - no refetch needed.
      }

      // The one exception: choosing "Send connection request" on a LinkedIn node
      // makes the server put a View profile step and a 1-day Delay in front of it,
      // in the same request. That structural change is not in the PATCH response:
      // the workflow is re-read in onSettled below until the new nodes arrive (the
      // canvas merges them in, keeping the open panel), and the user is told why
      // the steps were added.
      announceViewProfileStep(viewProfileStep);
      if (viewProfileStep?.added) {
        const workflowId = context?.workflowId ?? variables.workflowId;
        const newNodeIds = [viewProfileStep.viewNodeId, viewProfileStep.delayNodeId]
          .filter((id): id is number => typeof id === 'number');
        if (workflowId && newNodeIds.length > 0) {
          const pending = pendingViewProfileNodes.get(workflowId) || new Set<number>();
          newNodeIds.forEach((id) => pending.add(id));
          pendingViewProfileNodes.set(workflowId, pending);
        } else {
          invalidateWorkflowStructure(queryClient);
        }
      }
    },

    onSettled: (_data, _error, variables) => {
      resyncAwaitedStructure(queryClient, variables.workflowId);
    },
  });
};

/**
 * GH-1029: Fetch full email thread for a sequence
 * Lazy-loaded — only fetches when sequenceId is provided and enabled
 */
export const useGetEmailThread = (
  sequenceId: string | number | null,
  enabled: boolean = true,
) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['email-thread', sequenceId],
    queryFn: () => getEmailThread(sequenceId!),
    enabled: !!sequenceId && enabled && isAuthenticated,
  });
};
